import { memo, useState } from "react";
import { toast } from "sonner";
import { FileText, FileX2, FileCode, FileCode2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { invoiceService } from "../services/invoice.service";
import { downloadBlob } from "@/shared/lib/download";
import type { InvoiceResponseDTO } from "../types/invoice.types";

type DownloadKind = "pdf" | "pdf-canceled" | "xml" | "xml-canceled";

interface InvoiceDownloadActionsProps {
  invoice: InvoiceResponseDTO;
}

/**
 * Cluster de 4 botões de download por linha da tabela: PDF, PDF cancelado,
 * XML e XML cancelado.
 *
 * Cada botão fica desabilitado quando o arquivo correspondente não está
 * disponível (controlado pelas flags `hasPdf`, `hasCanceledPdf`, `hasXml`,
 * `hasCanceledXml`). Enquanto um download está em andamento, todos os
 * botões do cluster ficam desabilitados para evitar disparos paralelos.
 *
 * @example
 * <InvoiceDownloadActions invoice={invoice} />
 */
export const InvoiceDownloadActions = memo(function InvoiceDownloadActions({
  invoice,
}: InvoiceDownloadActionsProps) {
  const [downloadingKind, setDownloadingKind] = useState<DownloadKind | null>(
    null,
  );

  const trigger = async (
    kind: DownloadKind,
    fetcher: () => Promise<Blob>,
    filename: string,
  ) => {
    setDownloadingKind(kind);
    try {
      const blob = await fetcher();
      downloadBlob(blob, filename);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao baixar arquivo"));
    } finally {
      setDownloadingKind(null);
    }
  };

  const isBusy = downloadingKind !== null;

  return (
    <div className="flex gap-1">
      <Button
        variant="ghost"
        size="icon-sm"
        title="Baixar PDF"
        aria-label="Baixar PDF da nota fiscal"
        disabled={!invoice.hasPdf || isBusy}
        onClick={() =>
          trigger(
            "pdf",
            () => invoiceService.downloadPdf(invoice.id),
            `nota-fiscal-${invoice.id}.pdf`,
          )
        }
      >
        <FileText className="w-4 h-4" />
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        title="Baixar PDF de cancelamento"
        aria-label="Baixar PDF de cancelamento da nota fiscal"
        disabled={!invoice.hasCanceledPdf || isBusy}
        onClick={() =>
          trigger(
            "pdf-canceled",
            () => invoiceService.downloadCanceledPdf(invoice.id),
            `nota-fiscal-${invoice.id}-cancelada.pdf`,
          )
        }
      >
        <FileX2 className="w-4 h-4" />
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        title="Baixar XML"
        aria-label="Baixar XML da nota fiscal"
        disabled={!invoice.hasXml || isBusy}
        onClick={() =>
          trigger(
            "xml",
            () => invoiceService.downloadXml(invoice.id),
            `nota-fiscal-${invoice.id}.xml`,
          )
        }
      >
        <FileCode className="w-4 h-4" />
      </Button>

      <Button
        variant="ghost"
        size="icon-sm"
        title="Baixar XML de cancelamento"
        aria-label="Baixar XML de cancelamento da nota fiscal"
        disabled={!invoice.hasCanceledXml || isBusy}
        onClick={() =>
          trigger(
            "xml-canceled",
            () => invoiceService.downloadCanceledXml(invoice.id),
            `nota-fiscal-${invoice.id}-cancelada.xml`,
          )
        }
      >
        <FileCode2 className="w-4 h-4" />
      </Button>
    </div>
  );
});
